"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { isOwnProfileRoute } from "@/helpers/ProfileHelpers";
import useCapacitor from "@/hooks/useCapacitor";
import { useIdentity } from "@/hooks/useIdentity";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import UserPageTab from "./UserPageTab";
import { DEFAULT_USER_PAGE_TAB, USER_PAGE_TABS, USER_PAGE_TAB_IDS, type UserPageTabKey, type UserPageVisibilityContext, getUserPageTabByRoute } from "./userTabs.config";
import { shouldHideSubscriptions } from "./userPageVisibility";
import { shouldDelayUserPageBrainRedirect } from "./userPageBrainAccess";
const DEFAULT_TAB = DEFAULT_USER_PAGE_TAB;
const subscribeToClientRender = () => () => undefined;
const getClientRenderSnapshot = () => true;
const getServerRenderSnapshot = () => false;
const getVisibilityContext = ({
  showWaves,
  hasProfileWave,
  capacitorIsIos,
  country,
  isOwnProfile
}: {
  readonly showWaves: boolean;
  readonly hasProfileWave: boolean;
  readonly capacitorIsIos: boolean;
  readonly country: string | null | undefined;
  readonly isOwnProfile: boolean;
}): UserPageVisibilityContext => {
  return {
    showWaves,
    hasProfileWave,
    hideSubscriptions: shouldHideSubscriptions({
      capacitorIsIos,
      country
    }),
    isOwnProfile
  };
};
const resolveTabFromPath = (pathname: string): UserPageTabKey => {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] ?? "";
  const match = getUserPageTabByRoute(routeSegment);
  return match?.id ?? DEFAULT_TAB;
};
export default function UserPageTabs(t0) {
  const $ = _c(73);
  const {
    initialProfile
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  let t1;
  if ($[0] !== params.user) {
    t1 = params.user?.toString() ?? "";
    $[0] = params.user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOrWallet = t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.toString();
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const searchString = t2;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves,
    connectedProfile,
    fetchingProfile
  } = useAuth();
  const {
    address,
    connectionState
  } = useSeizeConnectContext();
  let t3;
  if ($[4] !== handleOrWallet || $[5] !== initialProfile) {
    t3 = {
      handleOrWallet,
      initialProfile
    };
    $[4] = handleOrWallet;
    $[5] = initialProfile;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    profile: viewedProfile
  } = useIdentity(t3);
  const resolvedViewedProfile = viewedProfile ?? initialProfile;
  let t4;
  if ($[7] !== connectedProfile || $[8] !== handleOrWallet) {
    t4 = isOwnProfileRoute({
      connectedProfile,
      handleOrWallet
    });
    $[7] = connectedProfile;
    $[8] = handleOrWallet;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isOwnProfile = t4;
  const hasProfileWave = Boolean(resolvedViewedProfile.profile_wave_id);
  let t5;
  if ($[10] !== capacitor.isIos || $[11] !== country || $[12] !== hasProfileWave || $[13] !== isOwnProfile || $[14] !== showWaves) {
    t5 = getVisibilityContext({
      showWaves,
      hasProfileWave,
      capacitorIsIos: capacitor.isIos,
      country,
      isOwnProfile
    });
    $[10] = capacitor.isIos;
    $[11] = country;
    $[12] = hasProfileWave;
    $[13] = isOwnProfile;
    $[14] = showWaves;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const visibilityContext = t5;
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const isClientHydrated = useSyncExternalStore(subscribeToClientRender, getClientRenderSnapshot, getServerRenderSnapshot);
  let t6;
  if ($[16] !== pathname) {
    t6 = resolveTabFromPath(pathname);
    $[16] = pathname;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const resolvedTabFromPath = t6;
  const preserveProxyTabWhileOwnershipLoads = fetchingProfile && !connectedProfile && resolvedTabFromPath === USER_PAGE_TAB_IDS.PROXY;
  let t7;
  if ($[18] !== address || $[19] !== connectedProfile || $[20] !== connectionState || $[21] !== fetchingProfile || $[22] !== isClientHydrated || $[23] !== resolvedTabFromPath) {
    t7 = resolvedTabFromPath === USER_PAGE_TAB_IDS.BRAIN && shouldDelayUserPageBrainRedirect({
      address,
      connectedProfile,
      connectionState,
      fetchingProfile,
      isClientHydrated
    });
    $[18] = address;
    $[19] = connectedProfile;
    $[20] = connectionState;
    $[21] = fetchingProfile;
    $[22] = isClientHydrated;
    $[23] = resolvedTabFromPath;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const shouldSuppressBrainRedirect = t7;
  const preserveBrainTabWhileAccessLoads = shouldSuppressBrainRedirect;
  let t8;
  if ($[25] !== preserveBrainTabWhileAccessLoads || $[26] !== preserveProxyTabWhileOwnershipLoads || $[27] !== visibilityContext) {
    t8 = USER_PAGE_TABS.filter(tab => {
      if (preserveProxyTabWhileOwnershipLoads && tab.id === USER_PAGE_TAB_IDS.PROXY) {
        return true;
      }
      if (preserveBrainTabWhileAccessLoads && tab.id === USER_PAGE_TAB_IDS.BRAIN) {
        return true;
      }
      return tab.isVisible ? tab.isVisible(visibilityContext) : true;
    });
    $[25] = preserveBrainTabWhileAccessLoads;
    $[26] = preserveProxyTabWhileOwnershipLoads;
    $[27] = visibilityContext;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const visibleTabs = t8;
  let t9;
  if ($[29] !== resolvedTabFromPath || $[30] !== visibleTabs) {
    let t10;
    if ($[32] !== resolvedTabFromPath) {
      t10 = tab_0 => tab_0.id === resolvedTabFromPath;
      $[32] = resolvedTabFromPath;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    t9 = visibleTabs.some(t10);
    $[29] = resolvedTabFromPath;
    $[30] = visibleTabs;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const resolvedTabIsVisible = t9;
  let t10;
  bb0: {
    if (resolvedTabIsVisible) {
      t10 = resolvedTabFromPath;
      break bb0;
    }
    const firstVisibleTab = visibleTabs[0]?.id;
    t10 = firstVisibleTab ?? DEFAULT_TAB;
  }
  const activeTab = t10;
  let t11;
  if ($[34] !== handleOrWallet || $[35] !== pathname || $[36] !== resolvedTabIsVisible || $[37] !== router || $[38] !== searchString || $[39] !== shouldSuppressBrainRedirect || $[40] !== visibleTabs[0] || $[41] !== visibleTabs.length) {
    t11 = () => {
      if (!visibleTabs.length || resolvedTabIsVisible || !handleOrWallet || shouldSuppressBrainRedirect) {
        return;
      }
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab) {
        return;
      }
      const fallbackPath = fallbackTab.route ? `/${handleOrWallet}/${fallbackTab.route}` : `/${handleOrWallet}`;
      if (fallbackPath === pathname) {
        return;
      }
      const nextUrl = searchString ? `${fallbackPath}?${searchString}` : fallbackPath;
      router.replace(nextUrl);
    };
    $[34] = handleOrWallet;
    $[35] = pathname;
    $[36] = resolvedTabIsVisible;
    $[37] = router;
    $[38] = searchString;
    $[39] = shouldSuppressBrainRedirect;
    $[40] = visibleTabs[0];
    $[41] = visibleTabs.length;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== handleOrWallet || $[44] !== pathname || $[45] !== resolvedTabIsVisible || $[46] !== router || $[47] !== searchString || $[48] !== shouldSuppressBrainRedirect || $[49] !== visibleTabs) {
    t12 = [handleOrWallet, pathname, resolvedTabIsVisible, router, searchString, shouldSuppressBrainRedirect, visibleTabs];
    $[43] = handleOrWallet;
    $[44] = pathname;
    $[45] = resolvedTabIsVisible;
    $[46] = router;
    $[47] = searchString;
    $[48] = shouldSuppressBrainRedirect;
    $[49] = visibleTabs;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  useEffect(t11, t12);
  let t13;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  const checkScroll = t13;
  let t14;
  let t15;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(container_0);
        if (contentContainer) {
          resizeObserver.observe(contentContainer);
        }
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver?.disconnect();
      };
    };
    t15 = [checkScroll];
    $[52] = t14;
    $[53] = t15;
  } else {
    t14 = $[52];
    t15 = $[53];
  }
  useEffect(t14, t15);
  let t16;
  if ($[54] !== visibleTabs.length) {
    t16 = () => {
      if (!visibleTabs.length) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[54] = visibleTabs.length;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== visibleTabs) {
    t17 = [checkScroll, visibleTabs];
    $[56] = visibleTabs;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  useEffect(t16, t17);
  let t18;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      container_1.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  const scrollLeft_0 = t18;
  let t19;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  const scrollRight = t19;
  let t20;
  if ($[60] !== activeTab || $[61] !== visibleTabs) {
    t20 = visibleTabs.map(tabConfig => <UserPageTab key={tabConfig.id} tab={tabConfig} activeTabId={activeTab} parentRef={scrollContainerRef} />);
    $[60] = activeTab;
    $[61] = visibleTabs;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t20) {
    t21 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4" aria-label="Tabs">{t20}</div></div>;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== canScrollLeft) {
    t22 = canScrollLeft && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-group tw-absolute tw-left-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-start tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[65] = canScrollLeft;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== canScrollRight) {
    t23 = canScrollRight && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-group tw-absolute tw-right-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-end tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[67] = canScrollRight;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t21 || $[70] !== t22 || $[71] !== t23) {
    t24 = <div className="tw-relative tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700">{t21}{t22}{t23}</div>;
    $[69] = t21;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
}